import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from '@headlessui/react';
import { useState } from 'react';
import { Button } from 'src/components/button';
import FormInput from 'src/components/form-input';
import FormSelect from 'src/components/form-select';
import FormSwitch from 'src/components/form-switch';
import { CloseIon } from 'src/components/icons/CloseIcon';
import UserCredentialsManager from 'src/components/user-credentials-manager';
import {
  IUserCredential,
  NtpSettingsType,
  GlobalSettingsType,
} from 'src/context/global/types';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useUndoRedo } from 'src/context/undo-redo/use-undo-redo';
import { ensureDefaultUsers } from 'src/utils/default-users';
import { twMerge } from 'tailwind-merge';

import {
  GlobalSettingsIgnitionSection,
  GlobalSettingsValidationError,
  GlobalSettingsRemoteLoggingSection,
} from './global-settings/global-settings-sections';

const tabs = [
  { name: 'Unused Ports', key: 'unused-ports' },
  { name: 'Ignition', key: 'ignition' },
  { name: 'Remote Logging', key: 'remote-logging' },
  { name: 'NTP', key: 'ntp' },
  { name: 'User Credentials', key: 'user-credentials' },
];

const ntpQueryIntervalOptions = [
  { label: '8 seconds', value: 3 },
  { label: '16 seconds', value: 4 },
  { label: '32 seconds', value: 5 },
  { label: '1 minute', value: 6 },
  { label: '2 minutes', value: 7 },
  { label: '4 minutes', value: 8 },
  { label: '8 minutes', value: 9 },
  { label: '17 minutes', value: 10 },
  { label: '34 minutes', value: 11 },
  { label: '68 minutes', value: 12 },
  { label: '136 minutes', value: 13 },
  { label: '273 minutes', value: 14 },
  { label: '546 minutes', value: 15 },
  { label: '1092 minutes', value: 16 },
];

export const globalSettingsDefault: GlobalSettingsType = {
  ignitionEnabled: false,
  ignitionTimeout: 1800,
  userCredentials: ensureDefaultUsers(),
  remoteLogging: {
    enabled: false,
    destination: '',
    protocol: 'udp',
    port: 514,
    mark: false,
    interval: 60,
  },
  ntp: {
    enable: false,
    queryInterval: 12,
    server: '192.168.1.1',
  },
  unusedPorts: {
    interface: false,
    poe: false,
  },
};

export default function DialogGlobalSettings() {
  const { setShowDialogs, globalSettings, setGlobalSettings } =
    useGlobalContext();
  const { takeSnapshot } = useUndoRedo();
  const [currentTab, setCurrentTab] = useState<string>(tabs[0].key);

  const [error, setError] = useState<GlobalSettingsValidationError>({});

  const [values, setValues] = useState<GlobalSettingsType>({
    ignitionEnabled:
      globalSettings?.ignitionEnabled || globalSettingsDefault.ignitionEnabled,
    ignitionTimeout:
      globalSettings?.ignitionTimeout || globalSettingsDefault.ignitionTimeout,
    userCredentials: ensureDefaultUsers(globalSettings?.userCredentials),
    remoteLogging: {
      enabled:
        globalSettings?.remoteLogging?.enabled ||
        globalSettingsDefault.remoteLogging?.enabled ||
        false,
      destination:
        globalSettings?.remoteLogging?.destination ||
        globalSettingsDefault.remoteLogging?.destination ||
        '',
      protocol:
        globalSettings?.remoteLogging?.protocol ||
        globalSettingsDefault.remoteLogging?.protocol ||
        'udp',
      port:
        globalSettings?.remoteLogging?.port ||
        globalSettingsDefault.remoteLogging?.port ||
        514,
      mark:
        globalSettings?.remoteLogging?.mark ||
        globalSettingsDefault.remoteLogging?.mark ||
        false,
      interval:
        globalSettings?.remoteLogging?.interval ||
        globalSettingsDefault.remoteLogging?.interval ||
        60,
    },
    ntp: {
      enable:
        globalSettings?.ntp?.enable ||
        globalSettingsDefault.ntp?.enable ||
        false,
      queryInterval:
        globalSettings?.ntp?.queryInterval ||
        globalSettingsDefault.ntp?.queryInterval ||
        12,
      server:
        globalSettings?.ntp?.server ||
        globalSettingsDefault.ntp?.server ||
        '192.168.1.1',
    },
    unusedPorts: {
      interface:
        globalSettings?.unusedPorts?.interface ??
        globalSettingsDefault.unusedPorts?.interface ??
        false,
      poe:
        globalSettings?.unusedPorts?.poe ??
        globalSettingsDefault.unusedPorts?.poe ??
        false,
    },
  });

  const handleChange = (key: keyof GlobalSettingsType, value: any) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    validate({ ...values, [key]: value });
  };

  const handleUserCredentialsChange = (userCredentials: IUserCredential[]) => {
    handleChange('userCredentials', userCredentials);
  };

  const handleNtpChange = (key: keyof NtpSettingsType, value: any) => {
    const updatedNtp = {
      ...values.ntp,
      [key]: value,
    };
    handleChange('ntp', updatedNtp);
  };

  const validate = (v: GlobalSettingsType) => {
    const errors: GlobalSettingsValidationError = {};

    // ignition timeout: integer from 60 to 86400
    if (
      v.ignitionTimeout &&
      (v.ignitionTimeout < 60 || v.ignitionTimeout > 86400)
    ) {
      errors.ignitionTimeout = 'Ignition Timeout must be between 60 and 86400';
    }

    // remote logging validation
    if (v.remoteLogging?.enabled) {
      // destination: required when enabled, max length 255
      if (
        !v.remoteLogging.destination ||
        v.remoteLogging.destination.trim() === ''
      ) {
        errors.remoteLoggingDestination =
          'Destination is required when remote logging is enabled';
      } else if (v.remoteLogging.destination.length > 255) {
        errors.remoteLoggingDestination =
          'Destination must not exceed 255 characters';
      }

      // port: number from 1 to 65535
      if (
        v.remoteLogging.port &&
        (v.remoteLogging.port < 1 || v.remoteLogging.port > 65535)
      ) {
        errors.remoteLoggingPort = 'Port must be between 1 and 65535';
      }

      // interval: number from 10 to 3600 (when periodic heartbeat is enabled)
      if (
        v.remoteLogging.mark &&
        v.remoteLogging.interval &&
        (v.remoteLogging.interval < 10 || v.remoteLogging.interval > 3600)
      ) {
        errors.remoteLoggingInterval =
          'Interval must be between 10 and 3600 seconds';
      }
    }

    // NTP validation
    if (v.ntp?.enable) {
      // server: required when enabled
      if (!v.ntp.server || v.ntp.server.trim() === '') {
        errors.ntpServer = 'NTP Server is required when NTP is enabled';
      } else if (v.ntp.server.length > 255) {
        errors.ntpServer = 'NTP Server must not exceed 255 characters';
      }

      // queryInterval: positive number (typically 1-3600 seconds)
      if (
        v.ntp.queryInterval !== undefined &&
        v.ntp.queryInterval < 3 &&
        v.ntp.queryInterval > 16
      ) {
        errors.ntpQueryInterval = 'Invalid query interval';
      }
    }

    setError(errors);
  };

  const onCancel = () => {
    setShowDialogs({ globalSettings: false });
  };

  const handleSave = () => {
    takeSnapshot();
    setGlobalSettings(values);
    setShowDialogs({ globalSettings: false });
  };

  return (
    <Dialog
      open
      onClose={onCancel}
      className="relative z-50"
      data-testid="dialog-global-settings"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            data-testid="dialog-global-settings-panel"
            className={twMerge([
              'relative transform overflow-hidden rounded-lg bg-white px-0 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 sm:px-0',
              'data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
            ])}
          >
            <DialogTitle
              as="h3"
              className="px-4 pb-4 text-xl font-bold text-gray-900"
            >
              Global Settings
            </DialogTitle>
            <div className="absolute right-4 top-4 px-4">
              <button
                type="button"
                onClick={onCancel}
                data-testid="dialog-global-settings-close-button"
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <CloseIon className="h-6 w-6" />
              </button>
            </div>

            {/* Tabs Navigation */}
            <div className="relative border-b border-blue-400 px-4 pb-0.5">
              <nav
                aria-label="Tabs"
                className="-mb-px flex space-x-4 overflow-x-auto"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.name}
                    data-testid={`dialog-global-settings-tab-${tab.key}`}
                    type="button"
                    aria-current={currentTab === tab.key ? 'page' : undefined}
                    className={twMerge([
                      'text-md whitespace-nowrap border-b-2 px-2 py-1 font-medium',
                      currentTab === tab.key
                        ? 'border-blue-400 bg-blue-400 text-white'
                        : 'border-transparent text-gray-500',
                    ])}
                    onClick={() => setCurrentTab(tab.key)}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="max-h-[80vh] space-y-6 overflow-y-auto px-4 pt-4">
              {currentTab === 'unused-ports' && (
                <div data-testid="dialog-global-settings-unused-ports">
                  <h2 className="text-base font-semibold text-gray-900">
                    Unused Ports Settings
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Settings for unconnected ports of ROQSTAR Managed Switches.
                  </p>
                  <div className="mt-4 flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <FormSwitch
                        id="unused-ports-interface"
                        name="unusedPortsInterface"
                        label="Link"
                        data-testid="dialog-global-settings-unused-ports-interface"
                        checked={values.unusedPorts?.interface ?? false}
                        onChange={(_value) =>
                          handleChange('unusedPorts', {
                            ...values.unusedPorts,
                            interface: _value,
                          })
                        }
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <FormSwitch
                        id="unused-ports-poe"
                        name="unusedPortsPoE"
                        label="PoE"
                        data-testid="dialog-global-settings-unused-ports-poe"
                        checked={values.unusedPorts?.poe ?? false}
                        onChange={(_value) =>
                          handleChange('unusedPorts', {
                            ...values.unusedPorts,
                            poe: _value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentTab === 'ignition' && (
                <div data-testid="dialog-global-settings-ignition">
                  <GlobalSettingsIgnitionSection
                    values={values}
                    error={error}
                    dataTestIdPrefix="dialog-global-settings"
                    onChange={(next) => {
                      setValues(next);
                      validate(next);
                    }}
                  />
                </div>
              )}

              {currentTab === 'remote-logging' && (
                <div
                  className="flex flex-col gap-2 py-2"
                  data-testid="dialog-global-settings-remote-logging"
                >
                  <GlobalSettingsRemoteLoggingSection
                    values={values}
                    error={error}
                    dataTestIdPrefix="dialog-global-settings"
                    onChange={(next) => {
                      setValues(next);
                      validate(next);
                    }}
                  />
                </div>
              )}

              {currentTab === 'ntp' && (
                <div
                  className="flex flex-col gap-2 py-2"
                  data-testid="dialog-global-settings-ntp"
                >
                  <h2 className="text-base font-semibold text-gray-900">
                    NTP Settings
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Configure Network Time Protocol (NTP) client settings for
                    time synchronization.
                  </p>
                  <div className="mt-4 flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <FormSwitch
                        id="ntp-enabled"
                        name="ntpEnabled"
                        label="Enabled"
                        data-testid="dialog-global-settings-ntp-enabled"
                        checked={values.ntp?.enable || false}
                        onChange={(_value) => handleNtpChange('enable', _value)}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <FormInput
                        id="ntp-server"
                        name="ntpServer"
                        label="NTP Server"
                        placeholder="hostname or IP address"
                        data-testid="dialog-global-settings-ntp-server"
                        value={values.ntp?.server || ''}
                        onChange={(e) =>
                          handleNtpChange('server', e.target.value)
                        }
                        error={error.ntpServer}
                        disabled={!values.ntp?.enable}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <FormSelect
                        id="ntp-query-interval"
                        name="ntpQueryInterval"
                        label="Query Interval"
                        data-testid="dialog-global-settings-ntp-query-interval"
                        value={values.ntp?.queryInterval?.toString() || ''}
                        onChange={(e) =>
                          handleNtpChange(
                            'queryInterval',
                            parseInt(e.target.value, 10) || 12,
                          )
                        }
                        options={ntpQueryIntervalOptions.map((option) => ({
                          label: option.label,
                          value: option.value.toString(),
                        }))}
                        disabled={!values.ntp?.enable}
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentTab === 'user-credentials' && (
                <div data-testid="dialog-global-settings-user-credentials">
                  <UserCredentialsManager
                    userCredentials={values.userCredentials || []}
                    onUserCredentialsChange={handleUserCredentialsChange}
                  />
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col gap-2 px-4 sm:flex-row sm:justify-end">
              <Button
                type="button"
                color="gray"
                onClick={onCancel}
                className="flex items-center justify-center"
                data-testid="dialog-global-settings-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={Object.keys(error).length > 0}
                className="flex items-center justify-center"
                onClick={handleSave}
                data-testid="dialog-global-settings-save-button"
              >
                Save
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
